import Link from "next/link";
import SEOConfig from "@/common/seo.config";
import { SITE } from "@/common/seo.config";
import { Footer, Header, SEO } from "@/components";
import {
  AudienceSplit,
  CtaBand,
  FaqList,
  LandingSection,
  PageHero,
  PrimaryButton,
  ProcessRail,
  SecondaryButton,
  SectionHeader,
} from "@/components/main/landing/landing-ui";
import {
  buildBreadcrumbList,
  buildFaqPage,
  buildHowToSchema,
  buildWebPage,
  pageSchemas,
} from "@/lib/schema";

const PHASES = [
  {
    title: "Discover",
    description:
      "Map the workflow, owners, systems, data, constraints, and success metrics. Decide whether AI is even the right lever.",
  },
  {
    title: "Design",
    description:
      "Define architecture, tool access, evaluation, review points, and the smallest pilot that can prove value.",
  },
  {
    title: "Pilot",
    description:
      "Build against real data and real users. Measure outcomes. Make a clear go or no-go recommendation.",
  },
  {
    title: "Produce and hand off",
    description:
      "Harden for production, document ownership, set monitoring, and transfer runbooks so your team can operate the system.",
  },
];

const DELIVERABLES = [
  {
    title: "Scoped problem statement",
    description:
      "A shared definition of the workflow, constraints, and what success looks like in 60 to 90 days.",
  },
  {
    title: "Architecture and risk notes",
    description:
      "How the system connects to your stack, where humans stay in the loop, and what could go wrong.",
  },
  {
    title: "Working pilot",
    description:
      "Software exercised on real samples, with evaluation results you can show stakeholders.",
  },
  {
    title: "Production package",
    description:
      "Deployment path, monitoring, documentation, and ownership so the system does not die after launch.",
  },
];

const RHYTHM = [
  {
    title: "Weekly working sessions",
    description:
      "Short, focused reviews with operators and technical counterparts. Decisions stay close to the work.",
  },
  {
    title: "Shared backlog and demos",
    description:
      "Visible progress against the success criteria you set in discovery. No surprise mid-project pivots.",
  },
  {
    title: "Explicit decision gates",
    description:
      "Pilot exit criteria are written before build accelerates. Go, iterate, or stop with evidence.",
  },
];

const ROLES = [
  {
    title: "Your side",
    items: [
      "Workflow owner",
      "Systems or data contact",
      "Operator reviewers for the pilot",
      "Security or compliance when required",
    ],
  },
  {
    title: "Our side",
    items: [
      "Engagement lead / architect",
      "AI and application engineers",
      "Integration support",
      "Documentation and handoff owner",
    ],
  },
];

const TIMELINES = [
  {
    title: "Discovery",
    description: "Usually days to a couple of weeks, depending on access and stakeholder availability.",
  },
  {
    title: "Focused pilot",
    description: "Often measured in weeks when sample data and review time are available.",
  },
  {
    title: "Production hardening",
    description: "Driven by integrations, environments, and governance reviews unique to your stack.",
  },
];

const FAQS = [
  {
    question: "How involved does our team need to be?",
    answer:
      "Expect operator time during discovery and pilot feedback, plus access for systems and data. We do not build in a vacuum.",
  },
  {
    question: "Can you work with our existing vendors?",
    answer:
      "Yes. Integrations and governance often involve your cloud, ERP, or security teams. We coordinate rather than replace them.",
  },
  {
    question: "What happens if the pilot fails?",
    answer:
      "You still leave with a clear diagnosis: data gaps, workflow issues, or product-market fit problems. That is cheaper than scaling a weak idea.",
  },
  {
    question: "Do you support after launch?",
    answer:
      "Yes. Many clients continue with iteration, evaluation, and feature expansion after the first production release.",
  },
];

export default function HowWeWorkPage() {
  const structuredData = pageSchemas([
    buildWebPage({
      path: "/how-we-work",
      name: SEOConfig.howWeWork.pageTitle,
      description: SEOConfig.howWeWork.pageDescription,
    }),
    buildHowToSchema({
      name: "How Nivalabs AI engagements work",
      description: SEOConfig.howWeWork.pageDescription,
      path: "/how-we-work",
      steps: PHASES,
    }),
    buildFaqPage(FAQS, { id: `${SITE.siteUrl}/how-we-work#faq` }),
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "How we work", path: "/how-we-work" },
    ]),
  ]);

  return (
    <>
      <SEO {...SEOConfig.howWeWork} structuredData={structuredData} />
      <Header />
      <main>
        <PageHero
          title="How we work"
          lead="A clear path from first conversation to a system your operations team can own. No open-ended experiments without a decision point."
          primaryCta={
            <PrimaryButton href="/contact">Start a conversation</PrimaryButton>
          }
          secondaryCta={
            <SecondaryButton href="/use-cases">Browse use cases</SecondaryButton>
          }
        />

        <LandingSection>
          <SectionHeader
            title="Engagement phases"
            lead="Every engagement follows the same spine. Depth and timeline flex with risk and integration complexity."
          />
          <ProcessRail steps={PHASES} />
        </LandingSection>

        <LandingSection muted>
          <SectionHeader
            title="What you walk away with"
            lead="Artifacts that support decisions, not just slides about AI potential."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {DELIVERABLES.map((item) => (
              <div key={item.title} className="border-t border-line pt-5">
                <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
                <p className="mt-2 text-[0.9875rem] leading-relaxed text-ink-muted">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </LandingSection>

        <LandingSection>
          <SectionHeader
            title="Cadence during delivery"
            lead="How collaboration feels week to week once work is underway."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {RHYTHM.map((item) => (
              <div key={item.title} className="border-t border-line pt-5">
                <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
                <p className="mt-2 text-[0.9875rem] leading-relaxed text-ink-muted">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </LandingSection>

        <LandingSection muted>
          <SectionHeader
            title="Who is in the room"
            lead="Clear roles keep discovery and pilots from stalling."
          />
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {ROLES.map((group) => (
              <div key={group.title} className="rounded-2xl border border-line bg-surface p-6">
                <h3 className="text-lg font-semibold text-ink">{group.title}</h3>
                <ul className="mt-4 space-y-2">
                  {group.items.map((item) => (
                    <li
                      key={item}
                      className="flex gap-3 text-[0.9875rem] text-ink-muted"
                    >
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1c8c80]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </LandingSection>

        <LandingSection>
          <SectionHeader
            title="Typical timelines"
            lead="Exact dates depend on access and governance. These ranges help set expectations."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {TIMELINES.map((item) => (
              <div key={item.title} className="border-t border-line pt-5">
                <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
                <p className="mt-2 text-[0.9875rem] leading-relaxed text-ink-muted">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </LandingSection>

        <LandingSection muted>
          <SectionHeader
            title="Working style"
            lead="We are a delivery partner for operational AI, not a general AI strategy shop."
          />
          <AudienceSplit
            forItems={[
              "You want a build path with evaluation and handoff",
              "Operators and owners can participate in discovery",
              "Systems access can be arranged for a pilot",
            ]}
            notForItems={[
              "You only want a workshop with no implementation",
              "There is no owner for the workflow after launch",
              "You need staff augmentation without a defined outcome",
            ]}
          />
        </LandingSection>

        <LandingSection>
          <SectionHeader
            title="Related reading"
            lead="More context before you book a call."
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link href="/use-cases" className="landing-btn-secondary">
              Use cases
            </Link>
            <Link href="/mvp-poc-development" className="landing-btn-secondary">
              MVP and PoC
            </Link>
            <Link href="/faq" className="landing-btn-secondary">
              FAQ
            </Link>
            <Link href="/about" className="landing-btn-secondary">
              About
            </Link>
          </div>
        </LandingSection>

        <LandingSection muted>
          <SectionHeader title="Common questions" />
          <FaqList items={FAQS} />
          <p className="mt-8 text-sm text-ink-muted">
            See also the{" "}
            <Link
              href="/faq"
              className="font-semibold text-[#1c8c80] no-underline hover:underline"
            >
              full FAQ
            </Link>
            .
          </p>
        </LandingSection>

        <CtaBand
          title="Ready to map a workflow?"
          description="Tell us the problem and systems involved. We will propose a discovery path."
          primaryText="Talk to an architect"
          secondaryText="Contact form"
          secondaryHref="/contact"
        />
      </main>
      <Footer />
    </>
  );
}
